import { forwardRef } from 'react';
import TextField, { TextFieldProps } from '@mui/material/TextField';
import InputAdornment from '@mui/material/InputAdornment';
import { styled, alpha } from '@mui/material/styles';

interface InputProps extends Omit<TextFieldProps, 'error' | 'variant'> {
  error?: string;
  startIcon?: React.ReactNode;
  endIcon?: React.ReactNode;
  step?: string | number;
  min?: string | number;
  max?: string | number;
}

const StyledTextField = styled(TextField)(({ theme }) => ({
  '& .MuiOutlinedInput-root': {
    backgroundColor: theme.palette.background.paper,
    borderRadius: 10,
    transition: 'all 0.2s ease',
    '& fieldset': {
      borderColor: theme.palette.surface.glassBorder,
      borderWidth: '1.5px',
    },
    '&:hover fieldset': {
      borderColor: alpha(theme.palette.text.primary, 0.2),
    },
    '&.Mui-focused fieldset': {
      borderColor: theme.palette.primary.main,
      borderWidth: '2px',
      boxShadow: `0 0 0 3px ${alpha(theme.palette.primary.main, 0.08)}`,
    },
    '&.Mui-error fieldset': {
      borderColor: theme.palette.error.main,
      boxShadow: `0 0 0 3px ${alpha(theme.palette.error.main, 0.08)}`,
    },
    '&.Mui-disabled': {
      backgroundColor: alpha(theme.palette.background.default, 0.5),
      '& fieldset': {
        borderColor: alpha(theme.palette.text.disabled, 0.2),
      },
    },
  },
  '& .MuiInputLabel-root': {
    color: theme.palette.text.secondary,
    fontWeight: 500,
    fontFamily: '"IBM Plex Sans", sans-serif',
    '&.Mui-focused': {
      color: theme.palette.primary.main,
    },
    '&.Mui-error': {
      color: theme.palette.error.main,
    },
  },
  '& .MuiInputBase-input': {
    padding: '14px 16px',
    fontFamily: '"IBM Plex Sans", sans-serif',
    color: theme.palette.text.primary,
    '&::placeholder': {
      color: alpha(theme.palette.text.secondary, 0.6),
      opacity: 1,
    },
  },
  '& .MuiFormHelperText-root': {
    marginLeft: 4,
    marginTop: 6,
    fontSize: '0.8125rem',
    fontFamily: '"IBM Plex Sans", sans-serif',
    '&.Mui-error': {
      color: theme.palette.error.main,
    },
  },
  '& .MuiInputAdornment-root': {
    color: theme.palette.text.secondary,
    '& .MuiSvgIcon-root': {
      fontSize: '1.25rem',
    },
  },
}));

export const Input = forwardRef<HTMLInputElement, InputProps>(
  ({ error, helperText, startIcon, endIcon, step, min, max, ...props }, ref) => {
    return (
      <StyledTextField
        inputRef={ref}
        fullWidth
        error={!!error}
        helperText={error || helperText}
        variant="outlined"
        InputProps={{
          startAdornment: startIcon ? (
            <InputAdornment position="start">{startIcon}</InputAdornment>
          ) : undefined,
          endAdornment: endIcon ? (
            <InputAdornment position="end">{endIcon}</InputAdornment>
          ) : undefined,
        }}
        inputProps={{
          step,
          min,
          max,
        }}
        {...props}
      />
    );
  }
);

Input.displayName = 'Input';
